import type { SavedView, SavedViewsPageConfig } from './types';
import { SHARED_VIEW_NAME_PARAM } from './types';

const matchesConfig = (key: string, config: SavedViewsPageConfig): boolean =>
  config.filterParamKeys.includes(key) ||
  config.filterParamPrefixes.some(prefix => key.startsWith(prefix));

/**
 * Returns the subset of `searchParams` that constitutes a view for this page —
 * every entry whose key is whitelisted (exact key or prefix class). Uses
 * `append`, so duplicate keys (should any future filter emit them) are all
 * preserved; today's comma-joined single params copy over verbatim.
 */
export const pickParamsByConfig = (
  searchParams: URLSearchParams,
  config: SavedViewsPageConfig,
): URLSearchParams => {
  const picked = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (matchesConfig(key, config)) picked.append(key, value);
  }
  return picked;
};

/** Serialize the current filters into a view's `query` string (no leading `?`). */
export const captureViewQuery = (
  searchParams: URLSearchParams,
  config: SavedViewsPageConfig,
): string => pickParamsByConfig(searchParams, config).toString();

/**
 * Order-independent normal form of a query: keys sorted, each key's values
 * sorted, joined into a single string. Two param sets that differ only in
 * ordering share a normal form.
 */
const normalizeParams = (params: URLSearchParams): string => {
  const byKey = new Map<string, string[]>();
  for (const [key, value] of params.entries()) {
    const list = byKey.get(key);
    if (list) list.push(value);
    else byKey.set(key, [value]);
  }
  return [...byKey.keys()]
    .sort()
    .map(key => `${key}=${[...byKey.get(key)!].sort().join(',')}`)
    .join('&');
};

/**
 * True when the page's current whitelisted params (already picked via
 * `pickParamsByConfig`) are the same set as a saved view's `query`. Comparison
 * is order-insensitive: `applyView` writes params in the view's saved order,
 * but the page re-serializes filters in its own order as the user interacts, so
 * a raw string compare would miss a match.
 */
export const queriesMatch = (currentPicked: URLSearchParams, viewQuery: string): boolean =>
  normalizeParams(currentPicked) === normalizeParams(new URLSearchParams(viewQuery));

/**
 * Build a shareable absolute URL for a view: origin + the current page path +
 * the view's filters + `sv_name` so the recipient's save prompt can pre-fill
 * the name.
 *
 * The path must be the live pathname (e.g. `/org-123/qc`), NOT the config's
 * `basePath` — QC/Analytics routes are org-scoped, and a bare `/qc` link would
 * land the recipient on an "Access Denied" page.
 */
export const buildShareUrl = (view: SavedView, origin: string, path: string): string => {
  const params = new URLSearchParams(view.query);
  params.set(SHARED_VIEW_NAME_PARAM, view.name);
  return `${origin}${path}?${params.toString()}`;
};

/** Read a shared view's suggested name from the URL (for pre-filling the save dialog). */
export const parseSvName = (searchParams: URLSearchParams): string =>
  searchParams.get(SHARED_VIEW_NAME_PARAM)?.trim() ?? '';
